import {Injectable} from '@angular/core';
import { EnvironmentService } from './environment.service';
import { HttpBackend, HttpClient } from '@angular/common/http';
import {Observable, catchError, throwError, timer} from 'rxjs';
import { saveAs } from 'file-saver';
import { filter, repeat, retry, switchMap, take } from 'rxjs/operators';
import { InitiateRequest } from '../models/initiateRequest';
import { UserTypes } from '../models/userTypes';
import {ValidationUtil} from "../utils/ValidationUtil";

@Injectable({
  providedIn: 'root',
})
export class CopyleaksServiceHelper {
  httpWithoutInterceptor: HttpClient;

  constructor(
      private envService: EnvironmentService,
      private http: HttpClient,
      private httpBackend: HttpBackend,
  ) {
    this.httpWithoutInterceptor = new HttpClient(this.httpBackend);
  }

  getBaseUrl(role: UserTypes){
    switch(role){
        case UserTypes.EMA:
            return this.envService.environment.emApiGatewayURL+"/em-app";
        case UserTypes.SAM:
            return this.envService.environment.samBaseUrl;
        default:
            return this.envService.environment.emApiGatewayURL;
    }
  }

  getCompleteResult(scanId:string,role:UserTypes): Observable<any> {
    let baseUrl = this.getBaseUrl(role);
    return this.http.get(baseUrl + '/copyleaks/' + scanId + '/completed')
        .pipe(
            catchError((err:any): any => {
                return err;
                // CZ: think about calling the error service from here
            }));
  }

  checkCompleteResult(scanId:string,role:UserTypes, retryCount: number, retryDelay: number): Observable<any> {
    let baseUrl = this.getBaseUrl(role);
    return this.http.get(baseUrl + '/copyleaks/' + scanId + '/completed')
        .pipe(
            retry({count: retryCount, delay: retryDelay}),
        );
  }

  getSource(scanId:string,role:UserTypes): Observable<any> {
    let baseUrl = this.getBaseUrl(role);
              return this.http.get(baseUrl + '/copyleaks/' + scanId + '/source')
                  .pipe(
                    catchError((err): any => {
                        return throwError(() => err)
                    })
                  );

  }

  getReport(scanId:string, reportId: string, role:UserTypes) {
    let baseUrl = this.getBaseUrl(role);
    return this.http.get(baseUrl  + '/copyleaks/' + scanId + '/results/' + reportId)
                  .pipe(
                      catchError((err:any): any => {
                          return err;
                      }));

  }


  getPdfReport(attachmentId: string, role:UserTypes) {
    let baseUrl = this.getBaseUrl(role);
    return this.http.get(baseUrl  + `/v1/attachment/${attachmentId}/report/presignedurl`, { responseType: 'text' })
                  .pipe(
                      catchError((err:any): any => {
                          return err;
                      })
                  )
                  .subscribe((data: any) => {
                      this.downloadFileWithPresignedUrl(data);
                  });
  }

  getStudentSubmissionAttachment(attachmentId: string, role:UserTypes) {
    let baseUrl = this.getBaseUrl(role);
    return this.http.get(baseUrl + `/v1/student/attachment/${attachmentId}/presignedurl`, { responseType: 'text' })
                  .pipe(
                      catchError((err:any): any => {
                          return err;
                      })
                  )
                  .subscribe((data: any) => {
                      this.downloadFileWithPresignedUrl(data);
                  });
  }

    getPDFReportByAttachmentId(attachmentId: string,scanId:string, role:UserTypes) {
        let baseUrl = this.getBaseUrl(role);
        return this.http.get(baseUrl  + `/v1/attachment/${attachmentId}/report/presignedurl`, { responseType: 'text' })
                    .pipe(
                        catchError((err:any): any => {
                            return err;
                        })
                    )
                    .subscribe((data: any) => {
                        this.downloadPDFWithPresignedUrl(data,scanId);
                    });
    }

  getExcludedIds(scanId: string, role:UserTypes) {
    let baseUrl = this.getBaseUrl(role);
    return this.http.get(baseUrl  + '/copyleaks/' + scanId + '/exclude-ids')
                  .pipe(
                      catchError((err:any): any => {
                          return err;
                      }));

  }

  getStudentCompare(taskId:string, studentId:string, attachmentId:string, role:UserTypes): Observable<any> {
    let baseUrl = this.getBaseUrl(role);
    return this.http.get(baseUrl  +`/copyleaks/${studentId.toLowerCase()}/${taskId}/${attachmentId}/compare/completed`)
                  .pipe(
                      catchError((err:any): any => {
                          return err;
                          // CZ: think about calling the error service from here
                      }));
  }

  getStudentSubmissions(studentId: string, role: UserTypes): Observable<any> {
    let baseUrl = this.getBaseUrl(role);
    let studentIdValidated = ValidationUtil.validate(studentId);
    let validatedPath = ValidationUtil.validate('/v2/submissions/student/' + studentIdValidated);
    return this.http.get(baseUrl  + validatedPath)
                  .pipe(
                      catchError((err:any): any => {
                          return err;
                      })
                  )
  }

  getTask(taskId:string, role:UserTypes): Observable<any> {
    let baseUrl = this.getBaseUrl(role);
    return this.http.get(baseUrl  +`/v1/tasks/${taskId}`)
                  .pipe(
                      catchError((err:any): any => {
                          return err;
                      }));
  }

  getComparison(attachment1: string, attachment2: string, role:UserTypes): Observable<any> {
    let baseUrl = this.getBaseUrl(role);
    return this.http.get(baseUrl  +
                  `/copyleaks/${attachment1}/to/${attachment2}/compare`).pipe(
                  catchError( (err:any) => {
                      console.log(err);
                      if (err.status === 404) {
                          console.log("Waiting for 15 seconds before retrying...");
                          return timer(15000).pipe(
                              switchMap(() => this.getComparison(attachment1, attachment2,role))
                          );
                      }
                      return throwError(err);
                  })
              );
  }


  downloadFileWithPresignedUrl(presignedUrl: string): void {
      this.httpWithoutInterceptor.get(presignedUrl, { responseType: 'blob' }).subscribe(
                  (data: Blob) => {
                      this.downloadFile(data);
                  },
                  (error:any) => {
                      console.error('Error downloading file', error);
                  },
                  () => {
                      console.log('File download completed');
                  }
              );


  }

    downloadPDFWithPresignedUrl(presignedUrl: string,scanId:string): void {
        this.httpWithoutInterceptor.get(presignedUrl, { responseType: 'blob' }).subscribe(
            (data: Blob) => {
                this.downloadPDF(data,scanId);
            },
            (error:any) => {
                console.error('Error downloading file', error);
            },
            () => {
                console.log('File download completed');
            }
        );
    }

    downloadZIPWithPresignedUrl(presignedUrl: string,scanId:string): void {
      this.httpWithoutInterceptor.get(presignedUrl, { responseType: 'blob' }).subscribe(
            (data: Blob) => {
                this.downloadZIP(data,scanId);
            },
            (error:any) => {
                console.error('Error downloading file', error);
            },
            () => {
                console.log('File download completed');
            }
        );
    }

  private downloadFile(data: Blob): void {
    const blob = new Blob([data], { type: 'application/octet-stream' });
    saveAs(blob, 'downloaded_file_name');
  }

    private downloadPDF(data: Blob,scanId:string): void {
        const blob = new Blob([data], { type: 'application/pdf' });
        saveAs(blob, scanId);
    }

    downloadZIP(data: Blob,scanId:string): void {
        const blob = new Blob([data], { type: 'application/zip' });
        saveAs(blob, scanId+"-database-matches");
    }


  updateExcludedResults(scanId: string, score: string, ids: string[],role:UserTypes){
      let body = {
      score: score,
      excludeSourceIds: ids
    }

    let baseUrl = this.getBaseUrl(role);
              return this.http.put(baseUrl  + '/copyleaks/' + scanId + '/score', body)
                  .pipe(
                      catchError((err:any) => {
                          return throwError(() => err)
                      })
                  );
  }

  getSubmissionAttachment(studentId: string, attachmentId: string, role:UserTypes): Observable<any> {
    let baseUrl = this.getBaseUrl(role);
    let studentIdValidated = ValidationUtil.validate(studentId);
    let attachmentIdValidated = ValidationUtil.validate(attachmentId);
    let sIdUnderscoreAIdValidated = studentIdValidated + '_' + attachmentIdValidated;
    let validatedPath = ValidationUtil.validate(baseUrl  + '/copyleaks/' + sIdUnderscoreAIdValidated + '/completed');
    return this.http.get(validatedPath)
                  .pipe(
                      retry({count: 20, delay: 30000}),
                      repeat({count: 20, delay: 30000}),
                      filter((data:any) => data.results),
                      take(1),
                      catchError((err:any): any => {
                          return throwError(() => err);
                      }),
                  )
  }

    getDBMatchesByScanId(scanId:string, role:UserTypes) {
        let baseUrl = this.getBaseUrl(role);
              return this.http.get(baseUrl  + `/v1/scan/${scanId}/db-matches/presignedurl`, { responseType: 'text' })
                    .pipe(
                        catchError((err:any): any => {
                            console.log("There is an error tyring to get DB matches for scanId: ", scanId)
                            return err;
                        })
                    )
                    .subscribe((data: any) => {
                        this.downloadZIPWithPresignedUrl(data,scanId);
                    });
    }

  initiateScan(request: InitiateRequest,role:UserTypes): Observable<any> {
    let baseUrl = this.getBaseUrl(role);
    return this.http.post(baseUrl  + '/copyleaks/initiate', request,  { responseType: 'text' })
                  .pipe(
                      catchError((err:any): any => {
                          return throwError(() => err)
                      })
                  );
  }

  compareAttachmentFiles(compareFileId: any, compareToFileId: any, role:UserTypes): Observable<any> {
    let baseUrl = this.getBaseUrl(role);
    let compare = {
        attachmentIdD1: compareToFileId,
        attachmentIdD2: compareFileId
    }

    return this.http.post(baseUrl + '/copyleaks/compare', compare,  { responseType: 'text' })
    .pipe(
        catchError((err): any => {
            return throwError(() => err)
        })
    );
  }

}